(() => {
  if (window.__hipipetCartRuntimeLoaded) return;
  window.__hipipetCartRuntimeLoaded = true;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const url = (path) => `${rootPath()}${path}`.replace(/([^:]\/)\/+/g, '$1');
  let busy = false;

  const drawer = () => document.getElementById('cart-drawer');
  const panel = () => drawer()?.querySelector('dialog');
  const stripAcceleratedCheckout = () => drawer()?.querySelectorAll('.additional-checkout-buttons').forEach((el) => el.remove());

  const rebuildQuantityControls = () => {
    const d = drawer();
    if (!d) return;

    d.querySelectorAll('tbody .cart-items__table-row[data-key]').forEach((row, index) => {
      const line = index + 1;
      const existing = row.querySelector('[data-hipipet-cart-qty]');

      if (existing) {
        existing.dataset.line = String(line);
        const existingValue = existing.querySelector('[data-hipipet-cart-qty-value]');
        if (existingValue) existingValue.dataset.cartLine = String(line);
        return;
      }

      const nativeControl = row.querySelector('cart-quantity-selector-component, quantity-selector-component, .quantity-selector');
      if (!nativeControl) return;

      const nativeInput = nativeControl.querySelector?.('input') || (nativeControl.matches?.('input') ? nativeControl : null);
      const quantity = Math.max(1, Number(nativeInput?.value || nativeInput?.getAttribute('value') || 1));
      const canUpdate = !nativeInput?.disabled && !nativeControl.closest('[aria-disabled="true"]');

      const replacement = document.createElement('div');
      replacement.className = 'quantity-selector hipipet-cart-quantity';
      replacement.setAttribute('data-hipipet-cart-qty', '');
      replacement.dataset.line = String(line);
      replacement.innerHTML = `
        <button type="button" data-hipipet-cart-qty-minus aria-label="Decrease quantity"${canUpdate ? '' : ' disabled'}>−</button>
        <input type="text" inputmode="numeric" readonly aria-label="Quantity" value="${quantity}" data-hipipet-cart-qty-value data-cart-line="${line}">
        <button type="button" data-hipipet-cart-qty-plus aria-label="Increase quantity"${canUpdate ? '' : ' disabled'}>+</button>
      `;

      nativeControl.replaceWith(replacement);
    });
  };

  const setExpanded = (open) => {
    document.querySelectorAll('[data-hipipet-cart-open], [aria-controls="cart-drawer"]').forEach((el) => {
      el.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  };

  const openDrawer = () => {
    const d = drawer();
    const p = panel();
    if (!d || !p) return;
    stripAcceleratedCheckout();
    rebuildQuantityControls();
    d.setAttribute('open', '');
    if (!p.open) {
      try { p.showModal(); } catch (_) { p.setAttribute('open', ''); }
    }
    document.documentElement.classList.add('hipipet-cart-open');
    setExpanded(true);
  };

  const closeDrawer = () => {
    const d = drawer();
    const p = panel();
    d?.removeAttribute('open');
    if (p?.open) {
      try { p.close(); } catch (_) { p.removeAttribute('open'); }
    }
    document.documentElement.classList.remove('hipipet-cart-open');
    setExpanded(false);
  };

  const parseAndReplaceCart = (html) => {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('#cart-drawer cart-items-component, cart-items-component[data-section-id="cart-drawer-section"], cart-items-component');
    const current = document.querySelector('#cart-drawer cart-items-component');
    if (next && current) {
      const nextPromo = doc.querySelector('#cart-drawer .hipipet-shipping-promo, .hipipet-shipping-promo');
      const currentPromo = document.querySelector('#cart-drawer .hipipet-shipping-promo');

      if (nextPromo && currentPromo) {
        currentPromo.replaceWith(nextPromo);
      } else if (nextPromo && !currentPromo) {
        current.before(nextPromo);
      } else if (!nextPromo && currentPromo) {
        currentPromo.remove();
      }

      current.replaceWith(next);
      stripAcceleratedCheckout();
      rebuildQuantityControls();
      return true;
    }
    return false;
  };

  const fetchDrawerSection = async () => {
    const joiner = location.search ? '&' : '?';
    const response = await fetch(`${location.pathname}${location.search}${joiner}section_id=cart-drawer-section`, {
      credentials: 'same-origin', headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(`Unable to refresh cart (${response.status})`);
    parseAndReplaceCart(await response.text());
  };

  const refreshDrawer = async (sections) => {
    const html = sections?.['cart-drawer-section'];
    if (!parseAndReplaceCart(html)) await fetchDrawerSection();
    stripAcceleratedCheckout();
    rebuildQuantityControls();
  };

  const addItems = async (items) => {
    const normalized = (items || []).map((item) => ({ ...item, id: Number(item.id), quantity: Math.max(1, Number(item.quantity || 1)) }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0);
    if (!normalized.length) throw new Error('No valid cart items supplied');
    const response = await fetch(url('cart/add.js'), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: normalized, sections: 'cart-drawer-section', sections_url: location.pathname })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.status) throw new Error(payload?.description || payload?.message || 'Cart add failed');
    await refreshDrawer(payload.sections || {});
    openDrawer();
    return payload;
  };

  const changeLine = async (line, quantity) => {
    const response = await fetch(url('cart/change.js'), {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity)), sections: 'cart-drawer-section', sections_url: location.pathname })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.status) throw new Error(payload?.description || payload?.message || 'Cart update failed');
    await refreshDrawer(payload.sections || {});
    openDrawer();
    return payload;
  };

  const removeLineFromTarget = (target) => {
    const attr = target.getAttribute('on:click') || target.closest('[on\\:click]')?.getAttribute('on:click') || '';
    const match = attr.match(/onLineItemRemove\/(\d+)/);
    if (match) return Number(match[1]);
    const input = target.closest('.cart-items__table-row')?.querySelector('input[data-cart-line]');
    return Number(input?.dataset?.cartLine);
  };

  const withBusy = async (fn) => {
    if (busy) return;
    busy = true;
    const d = drawer();
    d?.setAttribute('aria-busy', 'true');
    d?.querySelectorAll('[data-hipipet-cart-qty] button').forEach((button) => { button.disabled = true; });
    try {
      await fn();
    } catch (error) {
      console.error('[Hipipet cart]', error);
      alert(error?.message || 'Unable to update cart.');
    } finally {
      busy = false;
      d?.removeAttribute('aria-busy');
      rebuildQuantityControls();
      d?.querySelectorAll('[data-hipipet-cart-qty] button').forEach((button) => { button.disabled = false; });
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest('[data-hipipet-cart-open], [aria-controls="cart-drawer"]');
    if (trigger && !target.closest('#cart-drawer')) {
      event.preventDefault(); event.stopImmediatePropagation(); openDrawer(); return;
    }
    if (!target.closest('#cart-drawer')) return;
    if (target.closest('.theme-drawer__close-button, [data-cart-drawer-close]')) {
      event.preventDefault(); event.stopImmediatePropagation(); closeDrawer(); return;
    }
    const checkout = target.closest('#checkout, [name="checkout"]');
    if (checkout) {
      event.preventDefault(); event.stopImmediatePropagation(); window.location.assign(url('checkout')); return;
    }

    const quantityButton = target.closest('[data-hipipet-cart-qty-minus], [data-hipipet-cart-qty-plus]');
    if (quantityButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const control = quantityButton.closest('[data-hipipet-cart-qty]');
      const input = control?.querySelector('[data-hipipet-cart-qty-value]');
      const line = Number(control?.dataset?.line);
      const currentQuantity = Math.max(1, Number(input?.value || 1));
      const nextQuantity = quantityButton.hasAttribute('data-hipipet-cart-qty-plus')
        ? currentQuantity + 1
        : Math.max(0, currentQuantity - 1);
      if (line > 0) withBusy(() => changeLine(line, nextQuantity));
      return;
    }

    const button = target.closest('button');
    if (!button) return;
    const action = button.getAttribute('on:click') || '';
    const isRemove = /onLineItemRemove/.test(action) || button.matches('[data-remove], .cart-remove-button, .cart-items__remove');
    if (!isRemove) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const line = removeLineFromTarget(button);
    if (line) withBusy(() => changeLine(line, 0));
  }, true);

  document.addEventListener('click', (event) => {
    const p = panel();
    if (!p || event.target !== p || !p.open) return;
    const rect = p.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) closeDrawer();
  });

  stripAcceleratedCheckout();
  rebuildQuantityControls();
  window.HipipetCartDrawerApi = { addItems, changeLine, refreshDrawer, openDrawer, closeDrawer };
})();
